import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const Manager = () => {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  const [quizzes, setQuizzes] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [status, setStatus] = useState("");
  const [credits, setCredits] = useState(null);

  const handleUpdateProfile = async () => {
    const newname = prompt("Enter your name");
    if (newname) {
      try {
        const response = await axiosPrivate.post(
          "/updateprofile",
          JSON.stringify({ newname })
        );

        alert("Profile updated successfully");
        window.location.reload();
      } catch (err) {
        console.error(err);
        if (err?.response?.status === 400) {
          setErrMsg("Missing or invalid parameters");
        } else if (err?.response?.status === 401) {
          setErrMsg("Invalid or missing authorization token");
        } else if (err?.response?.status === 500) {
          setErrMsg("Failed to update profile due to an internal error");
        } else {
          setErrMsg(err?.response?.data?.message || err?.message);
        }
      }
    }
  };

  const fetchCredits = async () => {
    try {
      const response = await axiosPrivate.get("/manager/credits");
      setCredits(response?.data?.credits);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        const response = await axiosPrivate.get("/manager/quizzes");
        setSuccess(true);
        setQuizzes(response?.data?.quizzes);
      } catch (err) {
        console.error(err);
        if (err?.response?.status === 404) {
          setErrMsg("No quizzes found for the manager");
        } else if (err?.response?.status === 401) {
          setErrMsg("Unauthorized access");
        } else {
          setErrMsg(err?.response?.data?.message || err?.message);
        }
      }
    };
    fetchQuizzes();
    fetchCredits();
  }, []);

  useEffect(() => {
    setStatus(localStorage.getItem("userstatus"));
  }, []);

  const handleDeleteQuiz = async (quizid) => {
    try {
      const confirmDelete = window.confirm(
        "Are you sure you want to delete the quiz?"
      );
      if (!confirmDelete) {
        return;
      }
      localStorage.setItem("quizid", quizid);

      const response = await axiosPrivate.post(
        "/manager/deletequiz",
        JSON.stringify({
          quizid,
        })
      );

      setQuizzes((prev) => prev.filter((quiz) => quiz.quizid !== quizid));

      if (response.status === 200) {
        alert("Quiz deleted successfully");
      }
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 400) {
        setErrMsg("No emails sent or incorrect request format.");
      } else if (err?.response?.status === 401) {
        setErrMsg("Invalid or missing authorization token.");
      } else if (err?.response?.status === 404) {
        setErrMsg("Quiz not found or unauthorized access.");
      } else {
        setErrMsg("Failed to delete quiz due to an internal error.");
      }
      alert(errMsg);
    }
  };

  return (
    <>
      {status === "complete" || status === "verified" ? (
        <div
          className="fade-in"
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "2rem 1rem",
          }}
        >
          {/* Header Section */}
          <div style={{ marginBottom: "2rem" }}>
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                marginBottom: "0.5rem",
                background:
                  "linear-gradient(135deg, var(--primary-purple-light), var(--accent-cyan))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Manager Dashboard
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "1.125rem" }}>
              Manage your psychometric assessments and participants
            </p>
          </div>

          {/* Stats Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            {/* Total Quizzes Card */}
            <div
              className="card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(124, 58, 237, 0.2), rgba(6, 182, 212, 0.2))",
                borderColor: "var(--primary-purple)",
              }}
            >
              <h3
                style={{
                  fontSize: "0.875rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--text-muted)",
                  marginBottom: "0.5rem",
                }}
              >
                Total Quizzes
              </h3>
              <p
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  color: "var(--text-primary)",
                  marginBottom: "0",
                }}
              >
                {quizzes.length}
              </p>
            </div>

            {/* Total Participants Card */}
            <div
              className="card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(6, 182, 212, 0.2))",
                borderColor: "var(--success-green)",
              }}
            >
              <h3
                style={{
                  fontSize: "0.875rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--text-muted)",
                  marginBottom: "0.5rem",
                }}
              >
                Total Participants
              </h3>
              <p
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  color: "var(--text-primary)",
                  marginBottom: "0",
                }}
              >
                {quizzes.reduce((acc, quiz) => acc + quiz.numParticipants, 0)}
              </p>
            </div>

            {/* Credits Card */}
            <div
              className="card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(236, 72, 153, 0.2))",
                borderColor: "var(--warning-amber)",
              }}
            >
              <h3
                style={{
                  fontSize: "0.875rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--text-muted)",
                  marginBottom: "0.5rem",
                }}
              >
                Available Credits
              </h3>
              <p
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  color: "var(--text-primary)",
                  marginBottom: "0",
                }}
              >
                {credits ?? "..."}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ marginBottom: "2rem" }}>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                marginBottom: "1rem",
                color: "var(--text-primary)",
              }}
            >
              Quick Actions
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "1rem",
              }}
            >
              <button
                className="btn-primary"
                onClick={() => navigate("/manager/createquiz")}
                style={{ width: "100%" }}
              >
                ➕ Create Quiz
              </button>
              <button
                className="btn-secondary"
                onClick={() => handleUpdateProfile()}
                style={{ width: "100%" }}
              >
                👤 Update Profile
              </button>
            </div>
          </div>

          {/* Quizzes Table */}
          {success && quizzes.length > 0 && (
            <div>
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: "var(--text-primary)",
                }}
              >
                Your Quizzes
              </h2>
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: "16px",
                  border: "1px solid var(--border)",
                }}
              >
                <table style={{ width: "100%", minWidth: "700px" }}>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Quiz Name</th>
                      <th>Quiz Type</th>
                      <th>Participants</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quizzes.map((quiz, index) => (
                      <tr key={quiz.quizid}>
                        <td>{index + 1}</td>
                        <td
                          style={{
                            fontWeight: "600",
                            color: "var(--text-primary)",
                          }}
                        >
                          {quiz.quizname}
                        </td>
                        <td>
                          <span
                            style={{
                              padding: "0.25rem 0.75rem",
                              borderRadius: "9999px",
                              fontSize: "0.875rem",
                              background:
                                "rgba(124, 58, 237, 0.2)",
                              border: "1px solid var(--primary-purple)",
                              color: "var(--primary-purple-light)",
                            }}
                          >
                            {quiz.quiztype}
                          </span>
                        </td>
                        <td>{quiz.numParticipants}</td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              className="btn-primary"
                              style={{
                                fontSize: "0.875rem",
                                padding: "0.5rem 1rem",
                                minWidth: "auto",
                              }}
                              onClick={() => {
                                localStorage.setItem("quizid", quiz.quizid);
                                navigate("/manager/quizdetails");
                              }}
                            >
                              View Details
                            </button>
                            <button
                              className="btn-danger"
                              style={{
                                fontSize: "0.875rem",
                                padding: "0.5rem 1rem",
                                minWidth: "auto",
                              }}
                              onClick={handleDeleteQuiz.bind(this, quiz.quizid)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {success && quizzes.length === 0 && (
            <div
              className="card"
              style={{
                textAlign: "center",
                padding: "3rem 2rem",
              }}
            >
              <h3
                style={{
                  fontSize: "1.5rem",
                  marginBottom: "0.5rem",
                  color: "var(--text-primary)",
                }}
              >
                No Quizzes Yet
              </h3>
              <p style={{ color: "var(--text-muted)", marginBottom: "1.5rem" }}>
                Get started by creating your first psychometric assessment
              </p>
              <button
                className="btn-primary"
                onClick={() => navigate("/manager/createquiz")}
              >
                Create Your First Quiz
              </button>
            </div>
          )}
        </div>
      ) : (
        <section className="fade-in">
          <h1
            style={{
              fontSize: "2rem",
              fontWeight: "700",
              marginBottom: "1rem",
            }}
          >
            Complete Your Profile
          </h1>
          <p style={{ color: "var(--text-secondary)", marginBottom: "2rem" }}>
            Please update your profile details to access the dashboard
          </p>
          <button className="btn-primary" onClick={() => handleUpdateProfile()}>
            Update Profile
          </button>
        </section>
      )}
    </>
  );
};

export default Manager;
